import { Box, IconButton, Stack, styled } from '@mui/material';
import * as React from 'react';
import { PropsWithChildren } from 'react';
import { motion } from 'framer-motion';
import { Copy, ThumbsDown, ThumbsUp } from 'lucide-react';

interface AiBubbleProps {
  text?: string;
  showActions?: boolean;
}

export function AiBubble({ children, text, showActions }: PropsWithChildren<AiBubbleProps>) {
  const handleCopy = () => {
    if (text) navigator.clipboard.writeText(text).catch(() => {/* ignore */});
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <MsgRow $isUser={false} $hoverable={!!showActions}>
        <Bubble $isUser={false}>
          <BubbleText $isUser={false}>{children}</BubbleText>
        </Bubble>

        {showActions && (
          <ActionBar className={'msg-actions'}>
            <ActionBtn title={'Copy'} onClick={handleCopy}><Copy size={14} /></ActionBtn>
            <ActionBtn title={'Good response'}><ThumbsUp size={14} /></ActionBtn>
            <ActionBtn title={'Bad response'}><ThumbsDown size={14} /></ActionBtn>
          </ActionBar>
        )}
      </MsgRow>
    </motion.div>
  );
}

export function UserBubble({ children }: PropsWithChildren<unknown>) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <MsgRow $isUser={true} $hoverable={false}>
        <Bubble $isUser={true}>
          <BubbleText $isUser={true}>{children}</BubbleText>
        </Bubble>
      </MsgRow>
    </motion.div>
  );
}

// ─── Styled — matches apps/ai-consumer chat-bubble-stream.tsx ─────────────────

const MsgRow = styled(Box, {
  shouldForwardProp: (p) => p !== '$isUser' && p !== '$hoverable',
})<{ $isUser: boolean; $hoverable: boolean }>(({ $isUser, $hoverable }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: $isUser ? 'flex-end' : 'flex-start',
  ...($hoverable && { '&:hover .msg-actions': { opacity: 1, pointerEvents: 'auto' } }),
}));

const Bubble = styled(Box, {
  shouldForwardProp: (p) => p !== '$isUser',
})<{ $isUser: boolean }>(({ $isUser }) => ({
  maxWidth: '85%',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  overflowWrap: 'break-word',
  ...($isUser
    ? {
        borderRadius: '16px 2px 16px 16px',
        padding: 16,
        background: '#2563eb',
        border: 'none',
      }
    : {
        borderRadius: '4px 16px 16px 16px',
        padding: '10px 12px',
        background: 'rgba(239,246,255,0.5)',
        border: '1px solid #bfdbfe',
      }),
}));

const BubbleText = styled(Box, {
  shouldForwardProp: (p) => p !== '$isUser',
})<{ $isUser: boolean }>(({ $isUser }) => ({
  fontSize: 14,
  lineHeight: 1.6,
  color: $isUser ? '#ffffff' : '#374151',
  fontWeight: $isUser ? 500 : 400,
}));

const ActionBar = styled(Stack)({
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 150ms',
  flexDirection: 'row',
  gap: 2,
  marginTop: 4,
});

const ActionBtn = styled(IconButton)({
  width: 28,
  height: 28,
  borderRadius: 6,
  color: '#94a3b8',
  '&:hover': { background: '#f1f5f9', color: '#475569' },
});
